import { HttpClient, HttpHeaders } from '@angular/common/http';
import {
  AfterViewChecked,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  NgZone,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import {
  IChatboxApiResponse,
  IChatboxApiSource,
  IChatboxAgentResponse,
  IChatMessage,
} from 'app/models/interfaces';
import { SharedService } from 'app/services/shared.service';
import { ChatboxService } from 'app/services/chatbox.service';
import { ApiService } from 'app/services/api.service';
import { Subscription } from 'rxjs';

const INITIAL_RENDER_COUNT = 50;
const LOAD_MORE_STEP = 50;

interface IChatMessageView {
  from: 'user' | 'bot';
  text: string;
  sources?: IChatboxApiSource[];
  requiresConfirmation?: boolean;
}

@Component({
  selector: 'fuse-chatbox',
  templateUrl: './chatbox.component.html',
  styleUrls: ['./chatbox.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ChatboxComponent implements OnInit, OnDestroy, AfterViewChecked {
  @ViewChild('chatMessagesContainer') private chatMessagesContainer: ElementRef;
  public chatBoxOpen = false;
  public mode: 'ask' | 'action' = 'ask';
  public isActionMode = false;
  public chatMessage = '';

  // full history; only the last renderCount messages are rendered
  private chatMessages: IChatMessageView[] = [];
  public visibleMessages: IChatMessageView[] = [];
  public hasOlderMessages = false;
  private renderCount = INITIAL_RENDER_COUNT;

  // message waiting for a yes/no (action mode)
  private pendingConfirmationMessage: IChatMessageView | null = null;

  public bubblePosition = { bottom: 32, right: 32 };
  public windowPosition = { bottom: 0, right: 0 };
  private dragging = false;
  private dragMoved = false;
  private dragOffset = { x: 0, y: 0 };
  private windowBubbleOffset = { bottom: 0, right: 0 };

  public chatboxSize = { width: 340, height: 360 };
  private resizing = false;
  private resizeDirection: 'top' | 'left' | 'corner' | null = null;
  private resizeStart = { x: 0, y: 0, width: 0, height: 0 };

  public isLoadingChatHistory = false;
  public isPdfPage = false;
  private userLoggedInSubscription: Subscription;
  private routerSubscription: Subscription;
  private isInitialLoadOrLogin = true;
  private shouldScrollToBottom = false;
  private viewSyncScheduled = false;
  private destroyed = false;

  constructor(
    private http: HttpClient,
    public sharedService: SharedService,
    private chatboxService: ChatboxService,
    private api: ApiService,
    private ngZone: NgZone,
    private cdr: ChangeDetectorRef,
    private router: Router
  ) {}

  ngOnInit(): void {
    this.userLoggedInSubscription = this.api.userLoggedIn$.subscribe(() => {
      this.resetChatBoxInstanceAfterLogin();
      this.cdr.markForCheck();
    });

    // OnPush won't recheck isLoggedIn() on logout, so refresh on navigation
    this.routerSubscription = this.router.events.subscribe(event => {
      if (event instanceof NavigationEnd) {
        this.isPdfPage = event.urlAfterRedirects.includes('/pdf');
        this.cdr.markForCheck();
      }
    });
    this.isPdfPage = this.router.url.includes('/pdf');

    // outside Angular so resize doesn't run change detection on every event
    this.ngZone.runOutsideAngular(() => {
      window.addEventListener('resize', this.onWindowResize);
    });
  }

  ngAfterViewChecked(): void {
    if (this.shouldScrollToBottom) {
      this.shouldScrollToBottom = false;
      const el = this.chatMessagesContainer?.nativeElement;
      if (el) {
        el.scrollTop = el.scrollHeight;
      }
    }
  }

  ngOnDestroy() {
    this.destroyed = true;
    document.removeEventListener('mousemove', this.onDrag);
    document.removeEventListener('mouseup', this.stopDrag);
    window.removeEventListener('mousemove', this.onResizeMove);
    window.removeEventListener('mouseup', this.stopResize);
    window.removeEventListener('resize', this.onWindowResize);
    if (this.userLoggedInSubscription) {
      this.userLoggedInSubscription.unsubscribe();
    }
    if (this.routerSubscription) {
      this.routerSubscription.unsubscribe();
    }
  }

  private onWindowResize = () => {
    if (this.chatBoxOpen) {
      this.updateWindowPosition();
    }
    const bubbleSize = 56;
    const maxRight = window.innerWidth - bubbleSize;
    const maxBottom = window.innerHeight - bubbleSize;
    this.bubblePosition.right = Math.min(
      Math.max(this.bubblePosition.right, 0),
      maxRight
    );
    this.bubblePosition.bottom = Math.min(
      Math.max(this.bubblePosition.bottom, 0),
      maxBottom
    );
    this.scheduleViewSync();
  };

  getChatHistory(): void {
    this.setLoadingChatHistoryState(true);
    this.chatboxService.getChatHistory().subscribe(
      response => {
        this.setLoadingChatHistoryState(false);
        const messages: IChatMessage[] = response?.data || [];
        this.chatMessages = messages.map(msg => ({
          from: msg.type === 'ai' ? 'bot' : 'user',
          text: msg.content,
        }));
        this.renderCount = INITIAL_RENDER_COUNT;
        this.refreshVisibleMessages();
        this.cdr.markForCheck();
        this.scrollToLatestMessage();
      },
      () => {
        this.setLoadingChatHistoryState(false);
        this.cdr.markForCheck();
        this.scrollToLatestMessage();
      }
    );
  }

  public setLoadingChatHistoryState(isLoading: boolean): void {
    this.isLoadingChatHistory = isLoading;
  }

  private resetChatBoxInstanceAfterLogin(): void {
    this.isInitialLoadOrLogin = true;
    this.chatMessages = [];
    this.renderCount = INITIAL_RENDER_COUNT;
    this.pendingConfirmationMessage = null;
    this.refreshVisibleMessages();
    this.closeChatBox();
    this.isActionMode = false;
    this.mode = 'ask';
    this.chatMessage = '';
    this.bubblePosition = { bottom: 32, right: 32 };
    this.dragOffset = { x: 0, y: 0 };
    this.chatboxSize = { width: 500, height: 550 };
    this.resizeStart = { x: 0, y: 0, width: 0, height: 0 };
  }

  startDrag(event: MouseEvent) {
    this.dragging = true;
    this.dragMoved = false;
    this.dragOffset = { x: event.clientX, y: event.clientY };

    if (this.chatBoxOpen) {
      this.windowBubbleOffset = {
        bottom: this.windowPosition.bottom - this.bubblePosition.bottom,
        right: this.windowPosition.right - this.bubblePosition.right,
      };
    }

    this.ngZone.runOutsideAngular(() => {
      document.addEventListener('mousemove', this.onDrag);
      document.addEventListener('mouseup', this.stopDrag);
    });
  }

  onDrag = (event: MouseEvent) => {
    if (!this.dragging) return;
    const dx = event.clientX - this.dragOffset.x;
    const dy = event.clientY - this.dragOffset.y;
    if (Math.abs(dx) > 2 || Math.abs(dy) > 2) {
      this.dragMoved = true;
    }
    this.bubblePosition.right -= dx;
    this.bubblePosition.bottom -= dy;
    const bubbleSize = 56;
    const minRight = 0;
    const minBottom = 0;
    const maxRight = window.innerWidth - bubbleSize;
    const maxBottom = window.innerHeight - bubbleSize;

    this.bubblePosition.right = Math.min(
      Math.max(this.bubblePosition.right, minRight),
      maxRight
    );
    this.bubblePosition.bottom = Math.min(
      Math.max(this.bubblePosition.bottom, minBottom),
      maxBottom
    );

    if (this.chatBoxOpen) {
      this.windowPosition.right =
        this.bubblePosition.right + this.windowBubbleOffset.right;
      this.windowPosition.bottom =
        this.bubblePosition.bottom + this.windowBubbleOffset.bottom;

      const windowWidth = Math.max(this.chatboxSize.width, 500);
      const windowHeight = Math.max(this.chatboxSize.height, 550);
      const constrainedPosition = this.constrainToViewport(
        this.windowPosition.bottom,
        this.windowPosition.right,
        windowWidth,
        windowHeight
      );
      this.windowPosition.bottom = constrainedPosition.bottom;
      this.windowPosition.right = constrainedPosition.right;

      const adjustedOffset = {
        bottom: this.windowPosition.bottom - this.bubblePosition.bottom,
        right: this.windowPosition.right - this.bubblePosition.right,
      };
      if (
        Math.abs(adjustedOffset.bottom - this.windowBubbleOffset.bottom) > 1 ||
        Math.abs(adjustedOffset.right - this.windowBubbleOffset.right) > 1
      ) {
        this.bubblePosition.bottom =
          this.windowPosition.bottom - this.windowBubbleOffset.bottom;
        this.bubblePosition.right =
          this.windowPosition.right - this.windowBubbleOffset.right;

        this.bubblePosition.right = Math.min(
          Math.max(this.bubblePosition.right, minRight),
          maxRight
        );
        this.bubblePosition.bottom = Math.min(
          Math.max(this.bubblePosition.bottom, minBottom),
          maxBottom
        );
      }
    }

    this.dragOffset = { x: event.clientX, y: event.clientY };
    this.scheduleViewSync();
  };

  stopDrag = () => {
    document.removeEventListener('mousemove', this.onDrag);
    document.removeEventListener('mouseup', this.stopDrag);
    this.ngZone.run(() => {
      if (!this.dragMoved) {
        this.closeOrOpenChatBox();
      }
      this.dragging = false;
      this.dragMoved = false;
      this.windowBubbleOffset = { bottom: 0, right: 0 };
      this.cdr.markForCheck();
    });
  };

  public openChatBox(): void {
    this.chatBoxOpen = true;
    if (this.isInitialLoadOrLogin) {
      this.isInitialLoadOrLogin = false;
      this.getChatHistory();
    }
    this.updateWindowPosition();
    this.scrollToLatestMessage();
  }

  private updateWindowPosition(): void {
    const bubbleHeight = 56;
    const bubbleWidth = 56;
    const spacing = 16;
    const windowHeight = Math.max(this.chatboxSize.height, 550);
    const windowWidth = Math.max(this.chatboxSize.width, 500);

    const bubbleBottom = this.bubblePosition.bottom;
    const bubbleTop = bubbleBottom + bubbleHeight;
    const bubbleRight = this.bubblePosition.right;
    const bubbleLeft = bubbleRight + bubbleWidth;

    let bottom = 0;
    let right = 0;
    let foundPosition = false;

    if (!foundPosition) {
      bottom = bubbleTop + spacing;
      right = this.bubblePosition.right;

      if (bottom + windowHeight > window.innerHeight - 16) {
        bottom = Math.max(16, window.innerHeight - windowHeight - 16);
      }
      if (right + windowWidth > window.innerWidth - 16) {
        right = Math.max(16, window.innerWidth - windowWidth - 16);
      }

      if (
        !this.isOverlapping(
          bottom,
          right,
          windowHeight,
          windowWidth,
          bubbleBottom,
          bubbleRight,
          bubbleHeight,
          bubbleWidth
        )
      ) {
        foundPosition = true;
      }
    }

    if (!foundPosition) {
      right = bubbleLeft + spacing;
      bottom = this.bubblePosition.bottom;

      if (right + windowWidth > window.innerWidth - 16) {
        right = Math.max(16, window.innerWidth - windowWidth - 16);
      }
      if (bottom + windowHeight > window.innerHeight - 16) {
        bottom = Math.max(16, window.innerHeight - windowHeight - 16);
      }

      if (
        !this.isOverlapping(
          bottom,
          right,
          windowHeight,
          windowWidth,
          bubbleBottom,
          bubbleRight,
          bubbleHeight,
          bubbleWidth
        )
      ) {
        foundPosition = true;
      }
    }

    if (!foundPosition) {
      bottom = bubbleBottom - windowHeight - spacing;
      right = this.bubblePosition.right;

      if (bottom < 16) {
        bottom = 16;
      }
      if (right + windowWidth > window.innerWidth - 16) {
        right = Math.max(16, window.innerWidth - windowWidth - 16);
      }

      if (
        !this.isOverlapping(
          bottom,
          right,
          windowHeight,
          windowWidth,
          bubbleBottom,
          bubbleRight,
          bubbleHeight,
          bubbleWidth
        )
      ) {
        foundPosition = true;
      }
    }

    if (!foundPosition) {
      right = bubbleRight - windowWidth - spacing;
      bottom = this.bubblePosition.bottom;

      if (right < 16) {
        right = 16;
      }
      if (bottom + windowHeight > window.innerHeight - 16) {
        bottom = Math.max(16, window.innerHeight - windowHeight - 16);
      }

      if (
        !this.isOverlapping(
          bottom,
          right,
          windowHeight,
          windowWidth,
          bubbleBottom,
          bubbleRight,
          bubbleHeight,
          bubbleWidth
        )
      ) {
        foundPosition = true;
      }
    }

    if (!foundPosition) {
      bottom = bubbleTop + spacing;
      right = this.bubblePosition.right;

      if (
        this.isOverlapping(
          bottom,
          right,
          windowHeight,
          windowWidth,
          bubbleBottom,
          bubbleRight,
          bubbleHeight,
          bubbleWidth
        )
      ) {
        right = bubbleLeft + spacing;
        if (
          this.isOverlapping(
            bottom,
            right,
            windowHeight,
            windowWidth,
            bubbleBottom,
            bubbleRight,
            bubbleHeight,
            bubbleWidth
          ) ||
          right + windowWidth > window.innerWidth - 16
        ) {
          bottom = bubbleBottom - windowHeight - spacing;
          right = this.bubblePosition.right;
        }
      }

      if (bottom < 16) bottom = 16;
      if (right < 16) right = 16;
      if (bottom + windowHeight > window.innerHeight - 16) {
        bottom = Math.max(16, window.innerHeight - windowHeight - 16);
      }
      if (right + windowWidth > window.innerWidth - 16) {
        right = Math.max(16, window.innerWidth - windowWidth - 16);
      }
    }

    const constrainedPosition = this.constrainToViewport(
      bottom,
      right,
      windowWidth,
      windowHeight
    );
    bottom = constrainedPosition.bottom;
    right = constrainedPosition.right;

    if (
      this.isOverlappingWithBubble(bottom, right, windowHeight, windowWidth)
    ) {
      const nonOverlappingPosition = this.findNonOverlappingPosition(
        bottom,
        right,
        windowWidth,
        windowHeight,
        bubbleBottom,
        bubbleRight,
        bubbleHeight,
        bubbleWidth
      );
      bottom = nonOverlappingPosition.bottom;
      right = nonOverlappingPosition.right;

      const finalPosition = this.constrainToViewport(
        bottom,
        right,
        windowWidth,
        windowHeight
      );
      bottom = finalPosition.bottom;
      right = finalPosition.right;
    }

    this.windowPosition.bottom = bottom;
    this.windowPosition.right = right;
  }

  private constrainToViewport(
    bottom: number,
    right: number,
    width: number,
    height: number
  ): { bottom: number; right: number } {
    const actualWidth = Math.max(width, 500);
    const actualHeight = Math.max(height, 550);

    const maxRight = Math.max(0, window.innerWidth - actualWidth);
    const maxBottom = Math.max(0, window.innerHeight - actualHeight);
    const minRight = 0;
    const minBottom = 0;

    right = Math.max(minRight, Math.min(right, maxRight));

    bottom = Math.max(minBottom, Math.min(bottom, maxBottom));

    return { bottom, right };
  }

  private isOverlapping(
    windowBottom: number,
    windowRight: number,
    windowHeight: number,
    windowWidth: number,
    bubbleBottom: number,
    bubbleRight: number,
    bubbleHeight: number,
    bubbleWidth: number
  ): boolean {
    const windowRightEdge = window.innerWidth - windowRight;
    const windowLeftEdge = windowRightEdge - windowWidth;
    const windowBottomEdge = window.innerHeight - windowBottom;
    const windowTopEdge = windowBottomEdge - windowHeight;

    const bubbleRightEdge = window.innerWidth - bubbleRight;
    const bubbleLeftEdge = bubbleRightEdge - bubbleWidth;
    const bubbleBottomEdge = window.innerHeight - bubbleBottom;
    const bubbleTopEdge = bubbleBottomEdge - bubbleHeight;

    return !(
      windowLeftEdge >= bubbleRightEdge ||
      windowRightEdge <= bubbleLeftEdge ||
      windowTopEdge >= bubbleBottomEdge ||
      windowBottomEdge <= bubbleTopEdge
    );
  }

  private isOverlappingWithBubble(
    windowBottom: number,
    windowRight: number,
    windowHeight: number,
    windowWidth: number
  ): boolean {
    const bubbleHeight = 56;
    const bubbleWidth = 56;
    return this.isOverlapping(
      windowBottom,
      windowRight,
      windowHeight,
      windowWidth,
      this.bubblePosition.bottom,
      this.bubblePosition.right,
      bubbleHeight,
      bubbleWidth
    );
  }

  private findNonOverlappingPosition(
    currentBottom: number,
    currentRight: number,
    windowWidth: number,
    windowHeight: number,
    bubbleBottom: number,
    bubbleRight: number,
    bubbleHeight: number,
    bubbleWidth: number
  ): { bottom: number; right: number } {
    const spacing = 16;
    const bubbleTop = bubbleBottom + bubbleHeight;
    const bubbleLeft = bubbleRight + bubbleWidth;

    const positions = [
      { bottom: bubbleTop + spacing, right: bubbleRight },
      { bottom: bubbleBottom, right: bubbleLeft + spacing },
      { bottom: bubbleBottom - windowHeight - spacing, right: bubbleRight },
      { bottom: bubbleBottom, right: bubbleRight - windowWidth - spacing },
      { bottom: bubbleBottom, right: window.innerWidth - windowWidth - 16 },
      { bottom: bubbleBottom, right: 16 },
      { bottom: window.innerHeight - windowHeight - 16, right: bubbleRight },
      { bottom: 16, right: bubbleRight },
    ];

    for (const pos of positions) {
      if (
        !this.isOverlapping(
          pos.bottom,
          pos.right,
          windowHeight,
          windowWidth,
          bubbleBottom,
          bubbleRight,
          bubbleHeight,
          bubbleWidth
        )
      ) {
        return pos;
      }
    }

    return { bottom: currentBottom, right: currentRight };
  }

  public closeOrOpenChatBox(): void {
    if (this.chatBoxOpen) {
      this.closeChatBox();
    } else {
      this.openChatBox();
    }
  }

  public toggleMode(): void {
    this.mode = this.isActionMode ? 'action' : 'ask';
    const modeSwitchMessage =
      this.mode === 'action'
        ? 'Action mode activated. What do you want to do?'
        : 'Ask mode activated. What’s on your mind?';
    this.appendMessage({ from: 'bot', text: modeSwitchMessage });
  }

  public sendMessage(): void {
    if (this.chatMessage.trim()) {
      const messageToSend = this.chatMessage.trim();

      if (
        this.pendingConfirmationMessage !== null &&
        (messageToSend.toLocaleLowerCase() === 'yes' ||
          messageToSend.toLocaleLowerCase() === 'no')
      ) {
        this.handleConfirmation(messageToSend.toLocaleLowerCase() === 'yes');
        this.chatMessage = '';
        return;
      }
      const headers = new HttpHeaders().set('IsAuthorized', 'true');

      this.appendMessage({ from: 'user', text: messageToSend });

      const body = {
        query: messageToSend,
      };

      if (this.mode === 'action') {
        this.http
          .post<IChatboxAgentResponse>('/API/chatbox/agent', body, { headers })
          .subscribe({
            next: response => {
              const answer =
                response.data?.ai_response || 'No response from server';
              const botMessage: IChatMessageView = {
                from: 'bot',
                text: answer,
              };
              if (
                response.status === 200 &&
                this.mode === 'action' &&
                response.data?.requires_confirmation === true
              ) {
                botMessage.requiresConfirmation = true;
                this.pendingConfirmationMessage = botMessage;
              } else {
                this.pendingConfirmationMessage = null;
              }
              this.appendMessage(botMessage);
            },
            error: err => {
              console.error('Error sending message:', err);
            },
          });
        this.chatMessage = '';
      } else {
        this.http
          .post<IChatboxApiResponse>('/API/chatbox/ask', body, { headers })
          .subscribe({
            next: response => {
              const answer = response.data?.answer || 'No response from server';
              const sources: IChatboxApiSource[] = Array.isArray(
                response.data?.sources
              )
                ? response.data.sources.slice(0, 3).map(s => ({
                    title: s.title,
                    url: s.url,
                  }))
                : [];
              this.appendMessage({ from: 'bot', text: answer, sources });
            },
            error: err => {
              console.error('Error sending message:', err);
            },
          });
        this.chatMessage = '';
      }
    }
  }

  public handleConfirmation(confirmed: boolean): void {
    if (this.pendingConfirmationMessage) {
      this.pendingConfirmationMessage.requiresConfirmation = false;
      this.pendingConfirmationMessage = null;
    }
    this.appendMessage({ from: 'user', text: confirmed ? 'Yes' : 'No' });
    const body = {
      confirmation: confirmed ? 'Yes' : 'No',
    };
    const headers = new HttpHeaders().set('IsAuthorized', 'true');
    this.http
      .post<IChatboxAgentResponse>('/API/chatbox/confirm_action', body, {
        headers,
        withCredentials: true,
      })
      .subscribe({
        next: response => {
          const answer =
            response.data?.ai_response || 'No response from server';
          this.appendMessage({ from: 'bot', text: answer });
        },
        error: err => {
          console.error('Error sending message:', err);
        },
      });
  }

  private scrollToLatestMessage(): void {
    // actual scroll happens in ngAfterViewChecked, after the DOM updates
    this.shouldScrollToBottom = true;
  }

  // new array ref so OnPush picks up the change
  private appendMessage(message: IChatMessageView): void {
    this.chatMessages = [...this.chatMessages, message];
    this.refreshVisibleMessages();
    this.scrollToLatestMessage();
    this.cdr.markForCheck();
  }

  private refreshVisibleMessages(): void {
    const total = this.chatMessages.length;
    const count = Math.min(this.renderCount, total);
    this.visibleMessages =
      count >= total
        ? this.chatMessages
        : this.chatMessages.slice(total - count);
    this.hasOlderMessages = total > count;
  }

  public loadOlderMessages(): void {
    const el: HTMLElement | undefined =
      this.chatMessagesContainer?.nativeElement;
    const previousScrollHeight = el ? el.scrollHeight : 0;

    this.renderCount = Math.min(
      this.renderCount + LOAD_MORE_STEP,
      this.chatMessages.length
    );
    this.refreshVisibleMessages();
    this.cdr.markForCheck();

    // keep scroll position after older messages are prepended
    this.ngZone.runOutsideAngular(() => {
      requestAnimationFrame(() => {
        if (el) {
          el.scrollTop = el.scrollHeight - previousScrollHeight;
        }
      });
    });
  }

  public trackByMessage(
    _index: number,
    message: IChatMessageView
  ): IChatMessageView {
    return message;
  }

  // one detectChanges per frame while dragging/resizing
  private scheduleViewSync(): void {
    if (this.viewSyncScheduled) {
      return;
    }
    this.viewSyncScheduled = true;
    requestAnimationFrame(() => {
      this.viewSyncScheduled = false;
      if (!this.destroyed) {
        this.cdr.detectChanges();
      }
    });
  }

  public closeChatBox(): void {
    this.chatBoxOpen = false;
  }

  public startResize(
    event: MouseEvent,
    direction: 'top' | 'left' | 'corner'
  ): void {
    event.preventDefault();
    this.resizing = true;
    this.resizeDirection = direction;
    this.resizeStart = {
      x: event.clientX,
      y: event.clientY,
      width: this.chatboxSize.width,
      height: this.chatboxSize.height,
    };

    this.ngZone.runOutsideAngular(() => {
      window.addEventListener('mousemove', this.onResizeMove);
      window.addEventListener('mouseup', this.stopResize);
    });
  }

  private onResizeMove = (event: MouseEvent) => {
    if (!this.resizing || !this.resizeDirection) return;

    const dx = event.clientX - this.resizeStart.x;
    const dy = event.clientY - this.resizeStart.y;

    let newWidth = this.resizeStart.width;
    let newHeight = this.resizeStart.height;

    if (this.resizeDirection === 'top') {
      newHeight = this.resizeStart.height - dy;
    }
    if (this.resizeDirection === 'left') {
      newWidth = this.resizeStart.width - dx;
    }
    if (this.resizeDirection === 'corner') {
      newWidth = this.resizeStart.width - dx;
      newHeight = this.resizeStart.height - dy;
    }

    const maxWidth = Math.floor(window.innerWidth * 0.7);
    const maxHeight = Math.floor(window.innerHeight * 0.8);

    newWidth = Math.min(Math.max(newWidth, 250), maxWidth);
    newHeight = Math.min(Math.max(newHeight, 200), maxHeight);

    this.chatboxSize = { width: newWidth, height: newHeight };
    this.scheduleViewSync();
  };

  private stopResize = () => {
    this.resizing = false;
    this.resizeDirection = null;
    window.removeEventListener('mousemove', this.onResizeMove);
    window.removeEventListener('mouseup', this.stopResize);

    this.ngZone.run(() => {
      if (this.chatBoxOpen) {
        this.updateWindowPosition();
      }
      this.cdr.markForCheck();
    });
  };
}
